<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

import CatalogFilterLabels from '~/layers/catalog/components/CatalogFilterLabels.vue'
import CatalogFiltersDesktop from '~/layers/catalog/components/CatalogFiltersDesktop.vue'
import CatalogGrid from '~/layers/catalog/components/CatalogGrid.vue'
import CatalogGridTop from '~/layers/catalog/components/CatalogGridTop.vue'
import { useCatalogDataProvider } from '~/layers/catalog/composables/catalogDataProvider'
import { useCatalogEventBus } from '~/layers/catalog/composables/catalogEventBus'
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'
import { HeaderVariant } from '~/layers/ui/composables/layout'

const props = defineProps({
  type: {
    type: String as PropType<CatalogDataProviderType>,
    required: true,
  },
})
const gridStore = useCatalogGridStore()
const catalogStore = useCatalogStore()
const catalogEventBus = useCatalogEventBus()
const diamondViewMode = useCookie<'snippet' | 'table'>(
  'catalog_view_mode_diamonds',
  {
    default: () => 'snippet',
    sameSite: 'lax',
    watch: 'shallow',
  },
)
const isTableViewport = useMediaQuery('(min-width: 1440px)', { ssrWidth: 0 })
const isViewReady = ref(false)
const canSwitchView = computed(
  () => props.type === 'diamonds' && isTableViewport.value,
)

onMounted(() => {
  isViewReady.value = true
})
const isTableView = computed(
  () => canSwitchView.value && diamondViewMode.value === 'table',
)

watch(isTableView, (isTable, wasTable) => {
  if (
    !isTable ||
    wasTable ||
    props.type !== 'diamonds' ||
    !catalogStore.sort.code
  ) {
    return
  }

  const tableSortCodes = ['weight', 'color', 'clarity', 'price']

  if (!tableSortCodes.includes(catalogStore.sort.code)) {
    catalogStore.resetSort()
    catalogEventBus.emit('sort:updated')
  }
})
const { currentMeta, currentMetaPath, setMetaFields } = usePageMeta()
const { $api } = useNuxtApp()
const {
  init,
  fetch,
  setType,
  setBaseRequest,
  setCustomFetch,
  setShouldBuildItems,
  setShouldFetchAfterRouteApply,
  setUrlOptions,
  skipInitialFetch,
} = useCatalogDataProvider()
setType(props.type)
setBaseRequest({})
setCustomFetch(null)
setShouldBuildItems(true)
setShouldFetchAfterRouteApply(true)
setUrlOptions({ basePath: null, parseCategory: true })

await useAsyncData('catalog-init', async () => {
  await init()
  if (!skipInitialFetch.value) {
    await fetch()
  }
})

const route = useRoute()
const isJewelryRoot = computed(() => route.path === '/jewelry/')
const pageTitle = shallowRef(currentMeta.value.h1 || gridStore.pageTitle)

watchEffect(() => {
  const isProductPage = route.name?.toString().includes('pdp')

  if (!isProductPage && currentMetaPath.value === route.path) {
    pageTitle.value = currentMeta.value.h1 || gridStore.pageTitle
  }
})
const catalogFilters = useCatalogFilter()

useLayout().setHeaderVariant(HeaderVariant.Default)
const breadcrumbs = computed(() => {
  if (props.type === 'diamonds') {
    return [{ name: 'Бриллианты' }]
  }
  if (!isJewelryRoot.value) {
    return [
      {
        name: 'Ювелирные украшения',
        to: `/jewelry/`,
        onClick: catalogFilters.reset,
      },
      { name: gridStore.pageTitle },
    ]
  }
  return [{ name: 'Ювелирные украшения' }]
})

const { data } = await useAsyncData('config-meta', async () =>
  $api.meta.getConfigMeta(),
)
const { script } = useMetaJewelry(props.type, data.value?.data)

watch(
  () => route.path,
  () =>
    setMetaFields({
      script,
    }),
  { immediate: true },
)
</script>

<template>
  <div class="catalog">
    <template v-if="type === 'jewelry'">
      <CatalogHeader />
    </template>

    <div class="catalog__container">
      <UBreadcrumbs
        :items="breadcrumbs"
        is-center
      />

      <h1 class="catalog__title">{{ pageTitle }}</h1>

      <div class="catalog__header-wrapper">
        <CatalogGridTop
          :hide-sort="isTableView"
          :view-mode="canSwitchView ? diamondViewMode : undefined"
          @update:view-mode="diamondViewMode = $event"
        />

        <CatalogFilterLabels :type="type" />
      </div>

      <div
        v-show="isViewReady"
        class="catalog__wrap"
      >
        <div class="catalog__filter catalog_desktop">
          <CatalogFiltersDesktop />
        </div>
        <CatalogGrid
          :key="type + '.grid'"
          :table-view="isTableView"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '../assets/scss/catalog' as *;
</style>
